import { Controller } from "@hotwired/stimulus"

// Scroll-driven phone image swipe using CSS sticky + GSAP ScrollTrigger detection.
// CSS sticky keeps the phone pinned. ScrollTrigger (synced with Lenis) detects
// when text panels cross the viewport center and triggers horizontal slide animation.
export default class extends Controller {
  static targets = ["panel", "phoneContent"]

  connect() {
    if (!window.gsap || !window.ScrollTrigger) return

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.tweens = []
    this.triggers = []
    this.steps = this.panelTargets.map((p) => p.dataset.step)
    this.currentStep = this.steps[0]

    // Set initial positions: first image visible, rest off-screen right
    // With reduced motion, skip the horizontal offset — all frames stay in place
    this.phoneContentTargets.forEach((el) => {
      const idx = this.steps.indexOf(el.dataset.step)
      if (this.reducedMotion) {
        window.gsap.set(el, { xPercent: 0, opacity: idx === 0 ? 1 : 0 })
      } else {
        window.gsap.set(el, { xPercent: idx === 0 ? 0 : 100, opacity: 1 })
      }
    })

    // Per-panel ScrollTrigger for step transitions.
    // "center center" fires when the panel's center (where the text sits)
    // reaches the viewport center — the swap aligns with the visible text.
    this.panelTargets.forEach((panel) => {
      const step = panel.dataset.step
      const trigger = window.ScrollTrigger.create({
        trigger: panel,
        start: "center center",
        end: "center center",
        onEnter: () => this.activateStep(step),
        onEnterBack: () => this.activateStep(step),
      })
      this.triggers.push(trigger)
    })
  }

  disconnect() {
    this.tweens.forEach((tween) => {
      if (tween.scrollTrigger) tween.scrollTrigger.kill()
      tween.kill()
    })
    this.triggers.forEach((trigger) => trigger.kill())
    this.tweens = []
    this.triggers = []
  }

  activateStep(step) {
    if (step === this.currentStep) return

    // Kill previous step tweens to prevent unbounded array growth
    this.tweens.forEach((t) => t.kill())
    this.tweens = []

    const oldIndex = this.steps.indexOf(this.currentStep)
    const newIndex = this.steps.indexOf(step)
    const forward = newIndex > oldIndex

    this.phoneContentTargets.forEach((el) => {
      if (el.dataset.step === step) {
        if (this.reducedMotion) {
          this.tweens.push(
            window.gsap.to(el, { opacity: 1, xPercent: 0, duration: 0 })
          )
        } else {
          this.tweens.push(
            window.gsap.fromTo(
              el,
              { xPercent: forward ? 100 : -100 },
              { xPercent: 0, duration: 0.5, ease: "power2.inOut" }
            )
          )
        }
      } else if (el.dataset.step === this.currentStep) {
        if (this.reducedMotion) {
          this.tweens.push(
            window.gsap.to(el, { opacity: 0, xPercent: 0, duration: 0 })
          )
        } else {
          this.tweens.push(
            window.gsap.to(el, {
              xPercent: forward ? -100 : 100,
              duration: 0.5,
              ease: "power2.inOut",
            })
          )
        }
      }
    })

    this.currentStep = step
  }
}
